import { Link } from 'react-router-dom';
import { APP_VERSION } from '../lib/version';

// Franchisee-facing handbook for the dashboard, reached from the Menu.
//
// The body is a single static HTML string rather than JSX. It is long-form
// prose with no interpolation and no user input — the same shape as
// BLOG_GUIDE_HTML in ContentTab — so keeping it as markup avoids hand-
// converting several hundred lines of attributes and entities into JSX for no
// behavioural gain. Nothing here is user-supplied, so there is no injection
// surface. Styles live in global.css under .user-manual / .um-*, scoped so
// they cannot leak into the app chrome.
//
// Updating: edit MANUAL_HTML directly. When a release changes what a tab does,
// update the relevant section here as well as the What's New entry.

const MANUAL_HTML = `
<header class="um-masthead">
  <p class="um-eyebrow">Amada Senior Care &middot; Franchise Partners</p>
  <h1>The Marketing Dashboard, tab by tab</h1>
  <p class="um-lede">
    Everything Amada tracks about your website, your listings, your ads, your calls and your
    leads lives in one place. This handbook walks through each tab in the order it appears on
    your screen &mdash; what it shows, where the numbers come from, and what to do with them.
  </p>
  <p class="um-masthead-meta">
    <span>dashboard.amadaseniorcare.com</span>
    <span>Covers dashboard v. APP_VERSION_PLACEHOLDER</span>
  </p>
</header>

<section class="um-section" id="start">
  <div class="um-sec-head">
    <span class="um-sec-num">Before you start</span>
    <h2>Getting in</h2>
  </div>
  <p>
    Go to <strong>dashboard.amadaseniorcare.com</strong> and enter your email address. The
    dashboard recognises your account and asks for your password.
  </p>
  <ul>
    <li><strong>First time here?</strong> Enter your email and the dashboard emails you a setup link. Use it to choose a password.</li>
    <li><strong>Forgot your password?</strong> Use the reset link on the sign-in screen &mdash; a new one arrives by email.</li>
    <li><strong>Staying signed in.</strong> Your session lasts a week. After that, sign in again.</li>
  </ul>
  <p>
    What you see is scoped to your territory. Everything in this handbook is your own data &mdash; no
    other franchise partner's numbers are visible to you, and yours aren't visible to them.
  </p>
  <div class="um-note is-tip">
    <span class="um-note-label">Worth knowing</span>
    <p>
      The web address updates as you move around, so any tab can be bookmarked. Bookmark
      <span class="um-ui">Calls and Leads</span> and you land straight there next time.
    </p>
  </div>
</section>

<section class="um-section" id="top">
  <div class="um-sec-head">
    <span class="um-sec-num">Above the tabs</span>
    <h2>The top of every page</h2>
  </div>
  <p>
    Three things sit above the tab bar and stay with you no matter which tab you're on: any
    current announcements, the header bar, and your location profile.
  </p>

  <h3>Announcement banners</h3>
  <p>
    Coloured strips at the very top carry time-sensitive marketing news &mdash; a new dashboard
    release, or a Marketing in a Box that's open for ordering. Click through for the full
    write-up, or dismiss the strip with the <span class="um-ui">&times;</span>. Banners retire
    themselves on a schedule, but the pages they link to stay put, so you can come back to an
    announcement after the banner is gone.
  </p>

  <h3>The header bar</h3>
  <ul>
    <li><strong><span class="um-ui">v. APP_VERSION_PLACEHOLDER</span></strong> &mdash; the version tag next to the title. Click it for the release notes. See <a href="#beyond">Updates &amp; announcements</a>.</li>
    <li><strong><span class="um-ui">Menu</span></strong> &mdash; tools that live outside the tab set: this manual, and <a href="#nps">Net Promoter Score</a>.</li>
    <li><strong><span class="um-ui">View:</span></strong> &mdash; the territory picker. It only appears if you hold more than one AF number; search by AF number, city or name to switch between them.</li>
    <li><strong><span class="um-ui">Survey open</span></strong> &mdash; appears only while a Marketing in a Box survey is accepting answers. Click it to give marketing your input on an upcoming kit.</li>
    <li><strong>Your name and <span class="um-ui">Sign out</span></strong> &mdash; far right.</li>
  </ul>
  <div class="um-note is-tip">
    <span class="um-note-label">Multiple territories</span>
    <p>
      If some of your territories run out of the same office, the dashboard folds them together
      under one entry, marked <span class="um-pill is-b">+1 territory</span>. Tick
      <span class="um-ui">Show additional territories sharing an office</span> in the picker to see
      each one separately.
    </p>
  </div>

  <h3>Your location profile &mdash; and why it matters most</h3>
  <p>
    Directly under the header sits your Amada logo, your marketing name, the owner name and your
    AF number, followed by three cards. This strip is <strong>the master record every other tab
    depends on.</strong> If a value here is wrong, the damage shows up downstream: rankings check
    the wrong site, leads route to the wrong inbox, listings drift out of sync.
  </p>

  <div class="um-cards">
    <div class="um-card">
      <h5>Web Presence</h5>
      <dl>
        <dt>Vanity URL</dt><dd>The short, memorable address you put on print materials.</dd>
        <dt>Site URL</dt><dd>Your live microsite. This is the address the dashboard measures traffic and rankings against.</dd>
        <dt>Google Business Profile</dt><dd>Your Google listing.</dd>
      </dl>
    </div>
    <div class="um-card">
      <h5>Critical Info</h5>
      <dl>
        <dt>Webform Distribution Emails</dt><dd>Exactly who gets emailed when someone fills out a form on your website. Check this list is current.</dd>
        <dt>Location Address</dt><dd>Your office address from Zoho, cross-checked against your Yext directory listing.</dd>
      </dl>
    </div>
    <div class="um-card">
      <h5>PPC Campaign</h5>
      <dl>
        <dt>Active PPC?</dt><dd><span class="um-pill is-g">Yes &mdash; Active</span> or <span class="um-pill is-r">No</span>.</dd>
        <dt>CallRail Number</dt><dd>The tracking number on your paid campaigns.</dd>
        <dt>Landing Page</dt><dd>Where your paid clicks go.</dd>
      </dl>
    </div>
  </div>

  <h4>Reading the address check</h4>
  <div class="um-table-wrap">
    <table>
      <thead><tr><th>Indicator</th><th>Meaning</th><th>What to do</th></tr></thead>
      <tbody>
        <tr><td><span class="um-pill is-g">&#10004; Yext Matches Zoho</span></td><td>Your office address is identical everywhere it's published.</td><td>Nothing.</td></tr>
        <tr><td><span class="um-pill is-a">&#9888; Possible misspelling</span></td><td>The two records nearly match &mdash; likely a typo in a street or city name.</td><td>Hover for both versions, then submit a ticket to have it corrected.</td></tr>
        <tr><td><span class="um-pill is-r">&#10008; Yext &ne; Zoho</span></td><td>The two addresses genuinely differ.</td><td>Submit a ticket. Mismatched addresses hurt local search rankings.</td></tr>
        <tr><td><span class="um-pill is-b">(no Yext)</span></td><td>No directory listing is configured for this location.</td><td>Submit a ticket to get one set up.</td></tr>
      </tbody>
    </table>
  </div>
  <div class="um-note is-care">
    <span class="um-note-label">Do this first</span>
    <p>
      Read all three cards once, today. Confirm the site URL is right, the webform emails reach
      a person who checks them, and the address shows a green check. Anything wrong here quietly
      costs you leads. Report corrections through
      <span class="um-ui">Submit a Ticket</span> on the <a href="#t9">Important Links</a> tab.
    </p>
  </div>
</section>

<section class="um-section" id="t1">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 01</span>
    <h2>Live Website &amp; Reviews</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Source</span><span class="um-v">Your live site &middot; Google Maps &middot; Google reviews</span></div>
    <div><span class="um-k">Refresh</span><span class="um-v">Site is live; reviews nightly</span></div>
  </div>
  <p>
    Your landing page when you sign in. It answers one question: <em>what does a prospective
    client actually see when they look me up?</em>
  </p>
  <ul>
    <li><strong>Website Preview &mdash; Mobile View.</strong> Your live site at phone width. Most families searching for care are on a phone, so this is the view that matters.</li>
    <li><strong>Google Map card.</strong> Your business as it appears on Google Maps, pinned to your city and state.</li>
    <li><strong>Reviews.</strong> Your star rating, total review count, and your most recent reviews with the text people wrote.</li>
    <li><strong>Website Preview &mdash; Desktop View.</strong> The same site at full width, lower on the page.</li>
  </ul>

  <h3>The two review tools</h3>
  <p>
    At the bottom of the map card are the most practically useful controls in the whole
    dashboard:
  </p>
  <ol class="um-steps">
    <li><strong><span class="um-ui">Your Reviews Link</span></strong> copies a link that opens the review form on your Google listing. Paste it into a follow-up text, an email signature, or an invoice.</li>
    <li><strong><span class="um-ui">Download QR Code</span></strong> saves a QR image of that same link. Put it on a leave-behind, a business card, a flyer at a community event.</li>
  </ol>
  <div class="um-note is-tip">
    <span class="um-note-label">Why bother</span>
    <p>
      Review count and recency are among the strongest signals for whether you appear in
      Google's Maps 3-pack. If <a href="#t2">Metrics &rarr; Rankings</a> shows you outside the
      3-pack, this is the lever to pull first.
    </p>
  </div>
</section>

<section class="um-section" id="t2">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 02</span>
    <h2>Metrics</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Source</span><span class="um-v">Google Analytics &middot; DataForSEO &middot; Google Business Profile &middot; Yext</span></div>
    <div><span class="um-k">Refresh</span><span class="um-v">Nightly, with some panels weekly</span></div>
  </div>
  <p>Everything you earn without paying for a click. Four sub-tabs across the top.</p>

  <h3><span class="um-ui">Website</span> &mdash; who's visiting and how they found you</h3>
  <p>Four headline numbers, each comparing the last 90 days with the 90 days before it:</p>
  <div class="um-table-wrap">
    <table>
      <thead><tr><th>Metric</th><th>What it counts</th></tr></thead>
      <tbody>
        <tr><td class="um-t-name">Sessions</td><td>Visits to your website. One person can have several.</td></tr>
        <tr><td class="um-t-name">Chats</td><td>Chat conversations started on your site.</td></tr>
        <tr><td class="um-t-name">Phone Clicks</td><td>Taps on your phone number &mdash; a strong intent signal.</td></tr>
        <tr><td class="um-t-name">Webform Submissions</td><td>Contact and consultation forms completed.</td></tr>
      </tbody>
    </table>
  </div>
  <p>Below the numbers:</p>
  <ul>
    <li><strong>New Users (Last 90 Days)</strong> &mdash; first-time visitors by week. Watch the trend line, not any single week.</li>
    <li><strong>How People Find Your Website</strong> &mdash; the split between organic search, direct, referral, paid and social.</li>
    <li><strong>Where is Your Site Traffic Coming From?</strong> &mdash; visits by city. A good sanity check that your traffic is actually in your territory.</li>
    <li><strong>Most Popular Pages</strong> &mdash; which pages earn attention. Useful for deciding what to write more of.</li>
  </ul>
  <p>Hover the small &#9432; next to a metric name for a plain-English definition.</p>

  <h3><span class="um-ui">Rankings</span> &mdash; where you sit in Google</h3>
  <p>
    Four cards, one per search term families actually use: <em>senior care</em>,
    <em>senior home care</em>, <em>home care</em>, <em>caregivers</em>. Each card shows two
    separate results:
  </p>
  <ul>
    <li><strong>Organic Rank</strong> &mdash; your position in the blue-link results. Green is top 3, blue top 10, orange top 20, red means outside the top 100.</li>
    <li><strong>Maps 3-Pack</strong> &mdash; whether you appear in the three-business map box at the top of local results, and at which of the three positions.</li>
  </ul>
  <p>
    Underneath, the <strong>Geo Grid</strong> maps your 3-pack visibility across a grid of points
    around your office, so you can see how far your local visibility actually reaches. Switch
    keywords with the buttons above the grid. Dark blue is #1; red means you don't appear at that
    point at all.
  </p>
  <div class="um-note is-tip">
    <span class="um-note-label">If the 3-pack says No</span>
    <p>
      The yellow callout at the top of this sub-tab links to your reviews link and to AFI's
      guide, <em>12 Proven Ways to Improve Your GMB</em>. Reviews are the fastest route into the
      3-pack.
    </p>
  </div>

  <h3><span class="um-ui">Google Business Profile</span> &mdash; your listing's performance and health</h3>
  <p>
    The top half measures the last 90 days: total <strong>Views</strong>, views specifically from
    <strong>Maps</strong>, <strong>Activity</strong>, <strong>Driving Directions</strong> and
    <strong>Phone Calls</strong>, plus a breakdown of where people saw you and what they did next.
  </p>
  <p>
    The bottom half is the <strong>GBP Health Score</strong> &mdash; a scan of your listing scored out
    of 100 and labelled <span class="um-pill is-g">Strong</span> <span class="um-pill is-g">Good</span>
    <span class="um-pill is-a">Needs work</span> <span class="um-pill is-r">Weak</span>
    <span class="um-pill is-r">Critical</span>. It checks your categories, phone, website, hours,
    description, rating, review count, how recently you were reviewed, and photo count.
  </p>
  <p>
    Anything failing becomes an <strong>Action Item</strong> tagged
    <span class="um-pill is-r">HIGH</span> <span class="um-pill is-a">MED</span>
    <span class="um-pill is-b">LOW</span>. Work the high-priority items first &mdash; most are things you can
    fix yourself in Google in a few minutes.
  </p>

  <h3><span class="um-ui">Directory Listings</span> &mdash; your presence across the web</h3>
  <p>
    Amada publishes your name, address and phone number to dozens of directories through Yext.
    <strong>Facebook and Google Business Profile are the exceptions</strong> &mdash; the Amada
    marketing team manages those two directly, so the status Yext reports on them is just what
    Yext can see. Those cards are marked, and anything that needs changing on them goes to
    marketing, not into a Yext ticket.
    This sub-tab shows <strong>Live Listings</strong>, <strong>Total Listings</strong> and your
    <strong>Live Rate</strong>, then a card per publisher:
    <span class="um-pill is-g">Live</span> is published,
    <span class="um-pill is-a">Pending</span> is waiting on the publisher,
    <span class="um-pill is-b">Opted In</span> and <span class="um-pill is-b">Suppressed</span> are
    directory-side states. Click any card to see the listing itself.
  </p>
  <p>Consistent listings are what make search engines confident about who and where you are.</p>
</section>

<section class="um-section" id="t3">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 03</span>
    <h2>Paid Performance</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Source</span><span class="um-v">Google Ads &middot; Meta Marketing API</span></div>
    <div><span class="um-k">Refresh</span><span class="um-v">Nightly &middot; rolling 90 days</span></div>
  </div>
  <p>
    Everything you're paying for, split by channel: <span class="um-ui">Google Ads</span>,
    <span class="um-ui">Meta / Facebook</span>, and <span class="um-ui">YouTube</span> (reporting not
    connected yet).
  </p>

  <h3>The eight numbers, and which direction is good</h3>
  <div class="um-table-wrap">
    <table>
      <thead><tr><th>Metric</th><th>Meaning</th><th>Good</th></tr></thead>
      <tbody>
        <tr><td class="um-t-name">Clicks</td><td>People who clicked your ad.</td><td class="um-num">Up</td></tr>
        <tr><td class="um-t-name">CTR</td><td>Share of people who saw the ad and clicked. Measures whether the ad appeals.</td><td class="um-num">Up</td></tr>
        <tr><td class="um-t-name">Impressions</td><td>Times your ad was shown.</td><td class="um-num">Up</td></tr>
        <tr><td class="um-t-name">Conversions</td><td>Clicks that became an enquiry &mdash; a call or a form.</td><td class="um-num">Up</td></tr>
        <tr><td class="um-t-name">Conv. Rate</td><td>Share of clicks that converted. Measures whether the landing page works.</td><td class="um-num">Up</td></tr>
        <tr><td class="um-t-name">Cost / Conv</td><td>What each enquiry cost you. The number to watch.</td><td class="um-num">Down</td></tr>
        <tr><td class="um-t-name">Cost</td><td>Total spend in the period.</td><td class="um-num">Down</td></tr>
        <tr><td class="um-t-name">Avg. CPC</td><td>Average price per click.</td><td class="um-num">Down</td></tr>
      </tbody>
    </table>
  </div>
  <p>
    The dashboard colours the four cost metrics in reverse, so <strong>green always means
    good</strong> &mdash; a falling cost per enquiry shows green even though the number went down.
    Under the metrics, <strong>Campaign Breakdown</strong> lists each campaign with its type and
    status.
  </p>

  <h3>Meta / Facebook</h3>
  <p>
    The same eight metrics for Facebook and Instagram, plus <strong>Active Ad Creatives</strong> &mdash;
    the actual images and copy currently running, with a link to view each one live. Useful for
    checking your creative still fits your market.
  </p>

  <div class="um-note is-tip">
    <span class="um-note-label">Two ways forward</span>
    <p>
      <strong>No campaign running?</strong> The Google Ads sub-tab links straight to the PPC
      request form &mdash; that's how you start one.
    </p>
    <p>
      <strong>Campaign running and you want more detail?</strong> The banner at the top links to
      Jake Keller, Amada's paid media specialist, who can pull highest-performing zip codes and
      split-bidding detail that isn't shown here.
    </p>
  </div>
</section>

<section class="um-section" id="t4">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 04</span>
    <h2>Calls and Leads</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Source</span><span class="um-v">CallRail &middot; your website forms</span></div>
    <div><span class="um-k">Refresh</span><span class="um-v">Calls in real time &middot; web forms nightly</span></div>
  </div>
  <p>Every inbound enquiry your marketing produced, in one place. Two sub-tabs.</p>

  <h3><span class="um-ui">Call Tracking</span></h3>
  <p>Six numbers across the top, for whatever date range you've selected:</p>
  <div class="um-table-wrap">
    <table>
      <thead><tr><th>Metric</th><th>What it tells you</th></tr></thead>
      <tbody>
        <tr><td class="um-t-name">Total Calls</td><td>Every tracked call that reached you.</td></tr>
        <tr><td class="um-t-name">Answer Rate</td><td>Share of calls a person picked up. The single most controllable number here.</td></tr>
        <tr><td class="um-t-name">Quality Leads</td><td>Calls identified as genuine care enquiries, not spam or vendors.</td></tr>
        <tr><td class="um-t-name">Assessments Booked</td><td>Calls that ended with an in-home assessment on the calendar.</td></tr>
        <tr><td class="um-t-name">Interviews Scheduled</td><td>Caregiver recruiting calls that produced an interview.</td></tr>
        <tr><td class="um-t-name">Avg Duration</td><td>Average call length. Very short averages often mean calls aren't being handled.</td></tr>
      </tbody>
    </table>
  </div>
  <p>
    <strong>Call Volume</strong> charts calls by week; <strong>Conversion Funnel</strong> shows
    how many survive each stage from ringing to booked. Where the funnel narrows sharply is where
    your process is leaking.
  </p>

  <h4>Filtering and exporting</h4>
  <ol class="um-steps">
    <li>The date range defaults to <strong>1 January through today</strong>. Change both dates to look at a month or a week.</li>
    <li>Filter by status &mdash; <span class="um-pill is-g">Answered</span> <span class="um-pill is-r">Missed</span> <span class="um-pill is-a">Voicemail</span> <span class="um-pill is-b">IVR Only</span> &mdash; or by caller sentiment.</li>
    <li>Type in the search box and press <strong>Enter</strong> to search within the results.</li>
    <li><span class="um-ui">&#8681; Download CSV</span> exports every call matching your current filters, ready for Excel or Google Sheets.</li>
  </ol>
  <p>
    Sort the table by clicking <strong>Date</strong>, <strong>Caller</strong>,
    <strong>Duration</strong> or <strong>Source</strong>.
    <span class="um-pill is-b">IVR Only</span> means the caller reached your phone menu and no further &mdash;
    those are excluded from your answer rate.
  </p>

  <h4>Opening a single call</h4>
  <p>Click any row and a panel slides in from the right with:</p>
  <ul>
    <li>Date, status, caller name and number, duration and source</li>
    <li>Sentiment and CallRail tags such as <strong>Good Lead</strong> or <strong>Schedule booked</strong></li>
    <li>A talk-time bar showing how much your team spoke versus the caller</li>
    <li>The recording, playable in the browser</li>
    <li>An AI summary of the conversation, key details pulled from it, and the full transcript</li>
  </ul>
  <div class="um-note is-care">
    <span class="um-note-label">Handle with care</span>
    <p>
      Recordings, transcripts and the extracted details describe real people and their health
      circumstances. Treat them exactly as you treat any client record: view them for your own
      follow-up and coaching, and don't forward, download or paste them anywhere outside your
      office.
    </p>
  </div>

  <h3><span class="um-ui">Webform Leads</span></h3>
  <p>
    Every contact and consultation form submitted on your Amada website, mirrored here each night
    and matched to your territory &mdash; so you no longer need to log into WordPress or dig through
    email to find out who enquired.
  </p>
  <p>
    Date, name, phone, email, message and which form they used. Phone numbers and emails are
    clickable. Long messages collapse to two lines with <span class="um-ui">Show more</span>.
    <span class="um-ui">&#8681; Download CSV</span> exports the lot as a call list.
  </p>
</section>

<section class="um-section" id="t5">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 05</span>
    <h2>Ad Match Amount(s)</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Source</span><span class="um-v">AFI spend-match tiers</span></div>
    <div><span class="um-k">Refresh</span><span class="um-v">Updated by marketing; date shown at the foot of the tab</span></div>
  </div>
  <p>
    What Amada contributes toward your marketing spend, and the one place you tell us how to
    split your paid budget.
  </p>

  <h3>Paid Campaign Spend Match</h3>
  <p>
    Two figures: the monthly match for the territory you're currently viewing, and the combined
    monthly match across every territory you own. Where you hold more than one,
    <strong>Territory Breakdown</strong> itemises them.
  </p>

  <h3>Qualified Lead Program</h3>
  <p>
    Amada covers <strong>50% of your A Place for Mom lead cost</strong> &mdash; currently about
    <strong>$29</strong> of the <strong>$58</strong> per lead &mdash; up to a monthly cap that scales
    with each territory's annualised revenue. The tab shows how many matched leads per month
    you're eligible for and the maximum monthly amount covered.
  </p>
  <div class="um-note is-care">
    <span class="um-note-label">Read the cap correctly</span>
    <p>
      The lead numbers are a <strong>ceiling on what we'll match</strong>, not a promise of that
      many leads. To join the programme, use the enrolment contact shown in the purple box at the
      bottom of the card.
    </p>
  </div>

  <h3>Paid Campaign Budget Allocation</h3>
  <p>
    A slider per territory setting how your paid budget divides between Google Ads and Meta.
    Google is the default at 100%; drag left to move budget toward Meta, and the pie updates as
    you go.
  </p>
  <ol class="um-steps">
    <li>Drag the slider until the split reads the way you want it.</li>
    <li>The label switches to <strong>Unsaved change</strong>.</li>
    <li>Click <span class="um-ui">Save &amp; notify marketing</span>.</li>
  </ol>
  <div class="um-note is-stop">
    <span class="um-note-label">Important</span>
    <p>
      Saving sends a request to the marketing team &mdash; it does not change your ad accounts
      automatically. Your campaigns move once marketing applies the change. YouTube and TikTok
      are coming.
    </p>
  </div>
</section>

<section class="um-section" id="t6">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 06</span>
    <h2>Social</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Source</span><span class="um-v">CoSchedule &middot; your website's own social links</span></div>
    <div><span class="um-k">Refresh</span><span class="um-v">Social links re-detected every 12 hours</span></div>
  </div>
  <ul>
    <li><strong>Social Media Content Calendar</strong> &mdash; the shared Amada calendar showing what corporate marketing is posting and when. Use it to plan your own posts around campaign moments rather than duplicating them.</li>
    <li><strong>Facebook</strong> &mdash; your page's live timeline. The dashboard finds your page from the link on your own website, so if the panel says no page was found, the link is missing from your site. <span class="um-ui">Share Page to Get More Likes</span> posts your page for you.</li>
    <li><strong>Amada Corporate Instagram</strong> &mdash; the national feed, for reshareable material.</li>
    <li><strong>Other Social Links</strong> &mdash; Amada's YouTube, X and LinkedIn.</li>
  </ul>
</section>

<section class="um-section" id="t7">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 07</span>
    <h2>WelcomeHome</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Source</span><span class="um-v">Your WelcomeHome CRM</span></div>
    <div><span class="um-k">Refresh</span><span class="um-v">Trailing 90 days; last sync shown at the top</span></div>
  </div>
  <p>
    The other tabs count enquiries. This one follows them through to paying clients, so you can
    see which lead sources actually produce business rather than just volume.
  </p>

  <h3>Lead Source Effectiveness</h3>
  <p>
    A row per lead source, with each stage of your pipeline as a column: <strong>Leads</strong> &rarr;
    <strong>Assessment</strong> &rarr; <strong>Intake</strong> &rarr; <strong>Started Service</strong>, plus
    <strong>Lost / Closed</strong> and <strong>Avg Days to Care</strong>.
  </p>
  <ul>
    <li>Click any column heading to sort by it.</li>
    <li>Green means that source converts at least 10 points better than your own average; red means 10 points worse.</li>
    <li>A <strong>*</strong> next to a percentage flags a source with fewer than five leads &mdash; too small to read into. Untick the box in the header to hide them.</li>
    <li>The <strong>Total</strong> row at the bottom is your overall conversion, and the benchmark the colours are measured against.</li>
  </ul>
  <div class="um-note is-tip">
    <span class="um-note-label">The question to ask</span>
    <p>
      Not "which source sends the most leads?" but "which source sends leads that
      <em>start service</em>?" A source with 40 leads and a 5% start rate is worth less than one
      with 12 leads at 30%.
    </p>
  </div>

  <h3>Sales Hygiene</h3>
  <p>Three process metrics you fully control, each shown against the network median:</p>
  <div class="um-table-wrap">
    <table>
      <thead><tr><th>Metric</th><th>What it measures</th></tr></thead>
      <tbody>
        <tr><td class="um-t-name">Median speed-to-lead (business hours)</td><td>How long a new lead waits for your first response, counting only working hours. Lower is better.</td></tr>
        <tr><td class="um-t-name">Median speed-to-lead (raw clock)</td><td>The same wait on the wall clock, including evenings and weekends.</td></tr>
        <tr><td class="um-t-name">Avg activities through Assessment</td><td>How many touches it takes you to get a prospect to assessment. Higher generally means more persistent follow-up.</td></tr>
      </tbody>
    </table>
  </div>
  <p>Green is meaningfully better than the network median, red meaningfully worse.</p>
  <p>
    If your territory covers several WelcomeHome communities, sub-tabs across the top let you
    switch between them.
  </p>
</section>

<section class="um-section" id="t8">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 08</span>
    <h2>Content</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Source</span><span class="um-v">You &mdash; this tab is an input, not a report</span></div>
    <div><span class="um-k">Refresh</span><span class="um-v">Status updates as marketing reviews and publishes</span></div>
  </div>
  <p>
    How you get news and blog posts published on your own microsite without touching WordPress.
    Marketing reviews everything before it goes live.
  </p>

  <h3>Submitting a post</h3>
  <ol class="um-steps">
    <li><strong>Choose the type.</strong> <span class="um-ui">News &amp; Events</span> for announcements, awards and community events. <span class="um-ui">Blog Post</span> for helpful articles that earn search traffic.</li>
    <li><strong>Write it.</strong> Headline (100 characters max) and body. The toolbar gives you headings, bold, italic, lists and links. On a blog post you can also set an <strong>Author</strong> &mdash; it defaults to your location name &mdash; and use the <span class="um-ui">Embed</span> button to drop in a YouTube, X, Instagram, Vimeo or TikTok link as a real playable embed.</li>
    <li><strong>Check it.</strong> <span class="um-ui">Check spelling &amp; grammar</span> under the editor runs a conservative pass and lists each suggested fix. <span class="um-ui">Apply all</span> accepts them, or dismiss and edit by hand.</li>
    <li><strong>Add images.</strong> Up to six, 10 MB each, <strong>JPG, PNG or WebP</strong> (WebP is converted to JPG automatically; HEIC and other formats are not accepted, so save those as JPG first). On a blog post, pick one as the <strong>&#9733; Featured image</strong>. Then describe each photo in a sentence &mdash; that's what generates proper alt text for search and accessibility.</li>
    <li><strong>Add related links.</strong> Up to three reputable sources related to your story. Citing trusted sources helps search engines treat your news section as authoritative.</li>
    <li><strong>Choose when.</strong> Publish as soon as it's approved, or schedule it for a future date. Scheduled posts still need review first.</li>
    <li><strong>Submit for Review.</strong></li>
  </ol>
  <div class="um-note is-stop">
    <span class="um-note-label">Photos of people</span>
    <p>
      If your images show anyone &mdash; clients, families, caregivers, event attendees &mdash; you must tick
      the box confirming you hold a signed photo/video release for everyone pictured. AFI's
      release form is linked right there. This is required before a submission with images will
      go through.
    </p>
  </div>
  <div class="um-note is-tip">
    <span class="um-note-label">Before your first blog</span>
    <p>
      Open <span class="um-ui">&#128214; How to Write a Good Blog</span> at the top of the blog form. It's
      a short guide to writing posts that actually rank &mdash; writing from your own experience, using
      your local knowledge, quoting real people, and structuring for search.
    </p>
  </div>

  <h3>Tracking what you've submitted</h3>
  <p><strong>Your recent submissions</strong> at the bottom of the tab shows every submission and where it stands:</p>
  <div class="um-table-wrap">
    <table>
      <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
      <tbody>
        <tr><td><span class="um-pill is-a">Pending review</span></td><td>With marketing. No action needed.</td></tr>
        <tr><td><span class="um-pill is-b">Approved, working on upload.</span></td><td>Cleared review; being published now.</td></tr>
        <tr><td><span class="um-pill is-b">Scheduled</span></td><td>Approved or pending, and holding for the date you chose. The date is shown alongside.</td></tr>
        <tr><td><span class="um-pill is-g">Published</span></td><td>Live. The headline becomes a link straight to the published page.</td></tr>
        <tr><td><span class="um-pill is-r">Rejected</span></td><td>Not published &mdash; the reason appears in the Notes column. Address it and resubmit.</td></tr>
        <tr><td><span class="um-pill is-r">Publish failed</span></td><td>Something went wrong technically. Submit a ticket.</td></tr>
      </tbody>
    </table>
  </div>
</section>

<section class="um-section" id="t9">
  <div class="um-sec-head">
    <span class="um-sec-num">Tab 09</span>
    <h2>Important Links</h2>
  </div>
  <p>The tools you need that live outside the dashboard, in one place.</p>
  <div class="um-table-wrap">
    <table>
      <thead><tr><th>Link</th><th>What it's for</th></tr></thead>
      <tbody>
        <tr><td class="um-t-name">Canva</td><td>Thousands of ready-made Amada marketing materials and the digitised brand book.</td></tr>
        <tr><td class="um-t-name">Digital AirStrike Dashboard</td><td>Login for the chatbot on your website.</td></tr>
        <tr><td class="um-t-name">Amada Print Store</td><td>Print materials &mdash; and where you order <strong>Marketing-In-A-Box</strong>.</td></tr>
        <tr><td class="um-t-name">Amada Clothing Store</td><td>Branded apparel.</td></tr>
        <tr><td class="um-t-name">WelcomeHome CRM</td><td>Your CRM login &mdash; the source behind tab 07.</td></tr>
        <tr><td class="um-t-name">Submit a Ticket</td><td>The marketing ticketing system: report a dashboard bug, request a feature, ask for design work, or flag wrong data anywhere in this handbook.</td></tr>
      </tbody>
    </table>
  </div>
</section>

<section class="um-section" id="nps">
  <div class="um-sec-head">
    <span class="um-sec-num">Menu &rarr;</span>
    <h2>Net Promoter Score</h2>
  </div>
  <div class="um-source">
    <div><span class="um-k">Where</span><span class="um-v"><span class="um-ui">Menu</span> button, top-left</span></div>
    <div><span class="um-k">Source</span><span class="um-v">Anonymous responses to your own links</span></div>
  </div>
  <p>
    NPS measures how willing people are to recommend you. It sits outside the tab set because it
    covers three relationships at once, and because the answers come from outside your office.
  </p>

  <h3>Three links, three questions</h3>
  <p>Your location gets one shareable link per audience, each asking a single question:</p>
  <ul>
    <li><strong>Caregivers</strong> &mdash; how likely are they to recommend working here?</li>
    <li><strong>Clients &amp; families</strong> &mdash; how likely are they to recommend your care?</li>
    <li><strong>Referral sources</strong> &mdash; how likely are they to recommend partnering with you?</li>
  </ul>
  <p>
    Each card gives you the link with a <span class="um-ui">Copy</span> button and a QR code you can
    <span class="um-ui">Download QR</span> and put on a flyer, a leave-behind, or a caregiver
    noticeboard. Respondents don't log in. They pick a number from 0 to 10, optionally leave a
    comment, and they're done &mdash; about ten seconds.
  </p>

  <h3>Reading your score</h3>
  <p>
    NPS is the percentage of <strong>promoters</strong> (9&ndash;10) minus the percentage of
    <strong>detractors</strong> (0&ndash;6). Scores of 7&ndash;8 are <strong>passives</strong>: they count
    toward your response total but neither for nor against. The result lands between &minus;100 and
    +100.
  </p>
  <p>
    You get an <strong>Overall</strong> tile plus a separate tile per audience &mdash; which is the
    useful part, because delighted clients and frustrated caregivers cancel each other out in a
    single blended number. A tile shows a score only once it has <strong>five or more
    responses</strong>; below that it says so rather than showing a misleading number from a tiny
    sample.
  </p>
  <p>
    <strong>Recent comments</strong> lists what people wrote, newest first, tagged with the score
    it came with. That's where the actionable detail is.
  </p>

  <h3>Anonymity, and rotating a link</h3>
  <p>
    Responses collect <strong>no names, no email addresses and no phone numbers</strong> &mdash; there
    is nowhere to enter them. The link itself is a scrambled code that doesn't reveal your
    location or the audience. That's deliberate: people answer honestly when they can't be
    identified.
  </p>
  <p>
    If a link ends up somewhere you didn't intend &mdash; forwarded outside your territory, or posted
    publicly &mdash; click <span class="um-ui">Rotate link</span>. The old link stops accepting responses
    immediately and you get a fresh one. Everything already collected stays exactly where it is.
  </p>
</section>

<section class="um-section" id="beyond">
  <div class="um-sec-head">
    <span class="um-sec-num">Staying current</span>
    <h2>Updates &amp; announcements</h2>
  </div>

  <h3>What's new &mdash; version notes</h3>
  <p>
    The <span class="um-ui">v. APP_VERSION_PLACEHOLDER</span> tag beside the dashboard title is always there and always
    clickable. It opens the release notes: what shipped in the current version, written in plain
    language, with every earlier release archived below it as an expandable row. If a number moved
    or a tab changed shape, that's where the explanation is.
  </p>
  <p>
    When a new version ships, a banner appears at the top of your dashboard pointing at the notes.
    Dismiss it with the <span class="um-ui">&times;</span> and it stays gone; the version tag remains
    either way, so you can always get back.
  </p>

  <h3>Marketing announcements</h3>
  <p>
    Time-sensitive marketing news &mdash; a Marketing in a Box opening for orders, for instance &mdash; also
    appears as a banner, linking to a full announcement page. The banner retires itself after a
    few days, but the page stays, so news you skimmed on a busy morning is still there a week
    later.
  </p>

  <h3>Marketing in a Box surveys</h3>
  <p>
    When marketing is planning an upcoming kit, a <span class="um-ui">Survey open</span> badge
    appears in the header. Click it to answer &mdash; a few questions about what you'd actually use. It
    disappears when the window closes. If you hold several territories, you answer once per
    territory.
  </p>
</section>

<section class="um-section" id="refresh">
  <div class="um-sec-head">
    <span class="um-sec-num">Reference</span>
    <h2>Where every number comes from</h2>
  </div>
  <p>
    Nothing here is typed in by hand. Each panel pulls from a named system on its own schedule, so
    a number that looks stale usually just hasn't refreshed yet. Most panels print their source
    and last sync in small grey text at the bottom.
  </p>
  <div class="um-table-wrap">
    <table>
      <thead><tr><th>Where you see it</th><th>Comes from</th><th>Updated</th></tr></thead>
      <tbody>
        <tr><td class="um-t-name">Profile cards at the top</td><td>Zoho CRM, via the master franchise sheet</td><td>Every 10 minutes</td></tr>
        <tr><td class="um-t-name">Location address check</td><td>Zoho, compared against Yext</td><td>With the profile</td></tr>
        <tr><td class="um-t-name">Website preview</td><td>Your live site, loaded directly</td><td>Live</td></tr>
        <tr><td class="um-t-name">Reviews &amp; star rating</td><td>Google</td><td>Nightly</td></tr>
        <tr><td class="um-t-name">Organic &rarr; Website</td><td>Google Analytics</td><td>Nightly &middot; rolling 90 days</td></tr>
        <tr><td class="um-t-name">Organic &rarr; Rankings</td><td>DataForSEO (Google US desktop)</td><td>Nightly &middot; cached 24 hours</td></tr>
        <tr><td class="um-t-name">Organic &rarr; Geo Grid</td><td>DataForSEO</td><td>Weekly</td></tr>
        <tr><td class="um-t-name">Organic &rarr; GBP metrics</td><td>Google Business Profile</td><td>Nightly &middot; rolling 90 days</td></tr>
        <tr><td class="um-t-name">Organic &rarr; GBP Health Score</td><td>Google Places</td><td>Weekly</td></tr>
        <tr><td class="um-t-name">Organic &rarr; Directory Listings</td><td>Yext</td><td>Nightly &middot; cached 15 minutes</td></tr>
        <tr><td class="um-t-name">Paid &rarr; Google Ads</td><td>Google Ads</td><td>Nightly &middot; rolling 90 days</td></tr>
        <tr><td class="um-t-name">Paid &rarr; Meta</td><td>Meta Marketing API</td><td>Nightly &middot; rolling 90 days</td></tr>
        <tr><td class="um-t-name">Calls and Leads &rarr; Call Tracking</td><td>CallRail</td><td>As calls arrive</td></tr>
        <tr><td class="um-t-name">Calls and Leads &rarr; Webform Leads</td><td>Your website's forms</td><td>Nightly</td></tr>
        <tr><td class="um-t-name">Ad Match Amount(s)</td><td>AFI spend-match tiers</td><td>When marketing updates them</td></tr>
        <tr><td class="um-t-name">Social &rarr; Facebook panel</td><td>The social links on your own website</td><td>Every 12 hours</td></tr>
        <tr><td class="um-t-name">WelcomeHome</td><td>Your WelcomeHome CRM</td><td>Synced; time shown on the tab</td></tr>
        <tr><td class="um-t-name">Net Promoter Score</td><td>Responses to your survey links</td><td>Immediately</td></tr>
      </tbody>
    </table>
  </div>
  <div class="um-note is-care">
    <span class="um-note-label">If something looks wrong</span>
    <p>
      Check the grey footnote on that panel first &mdash; a "last sync" from three days ago explains a
      lot. If the source and timing look right but the number still doesn't, submit a ticket and
      say which tab, which number, and what you expected.
    </p>
  </div>
</section>

<section class="um-section" id="help">
  <div class="um-sec-head">
    <span class="um-sec-num">Support</span>
    <h2>Getting help</h2>
  </div>
  <div class="um-cards">
    <div class="um-card">
      <h5>Anything about the dashboard</h5>
      <p><span class="um-ui">Submit a Ticket</span> on the <a href="#t9">Important Links</a> tab. Bugs, wrong data, feature requests and design work all go through it.</p>
    </div>
    <div class="um-card">
      <h5>Starting a paid campaign</h5>
      <p>The <a href="#t3">Paid &rarr; Google Ads</a> sub-tab links to the PPC request form when no campaign is running.</p>
    </div>
    <div class="um-card">
      <h5>Deeper paid-media analysis</h5>
      <p>Jake Keller, Amada's paid media specialist &mdash; linked from the top of the <a href="#t3">Paid Performance</a> tab.</p>
    </div>
    <div class="um-card">
      <h5>Qualified Lead Program</h5>
      <p>The enrolment contact and email are shown in the purple box on <a href="#t5">Ad Match Amount(s)</a>.</p>
    </div>
  </div>
  <footer class="um-footer">
    <p>
      Amada Senior Care &middot; Marketing Dashboard Handbook &middot; covers dashboard v. APP_VERSION_PLACEHOLDER. When the
      dashboard changes, the <span class="um-ui">v.</span> tag in the header is always the current
      record &mdash; click it for the latest release notes.
    </p>
  </footer>
</section>
`;

export default function UserManual() {
  // The manual quotes the running version in three places; substitute rather
  // than hard-coding so a version bump can't leave the handbook stale.
  const html = MANUAL_HTML.replaceAll('APP_VERSION_PLACEHOLDER', APP_VERSION);

  return (
    <div className="user-manual">
      <div className="um-topbar">
        <Link to="/" className="um-back">
          &larr; Back to dashboard
        </Link>
      </div>
      <div className="um-shell">
        <nav className="um-rail" aria-label="Contents">
          <p className="um-rail-title">Contents</p>
          <ul>
            <li>
              <a href="#start">
                <span className="um-rail-num">&mdash;</span>
                <span>Getting in</span>
              </a>
            </li>
            <li>
              <a href="#top">
                <span className="um-rail-num">&mdash;</span>
                <span>The top of every page</span>
              </a>
            </li>
          </ul>
          <hr className="um-rail-div" />
          <p className="um-rail-title">The nine tabs</p>
          <ol>
            {[
              ['#t1', '01', 'Live Website & Reviews'],
              ['#t2', '02', 'Metrics'],
              ['#t3', '03', 'Paid Performance'],
              ['#t4', '04', 'Calls and Leads'],
              ['#t5', '05', 'Ad Match Amount(s)'],
              ['#t6', '06', 'Social'],
              ['#t7', '07', 'WelcomeHome'],
              ['#t8', '08', 'Content'],
              ['#t9', '09', 'Important Links'],
            ].map(([href, num, label]) => (
              <li key={href}>
                <a href={href}>
                  <span className="um-rail-num">{num}</span>
                  <span>{label}</span>
                </a>
              </li>
            ))}
          </ol>
          <hr className="um-rail-div" />
          <ul>
            {[
              ['#nps', 'Net Promoter Score'],
              ['#beyond', 'Updates & announcements'],
              ['#refresh', 'Where every number comes from'],
              ['#help', 'Getting help'],
            ].map(([href, label]) => (
              <li key={href}>
                <a href={href}>
                  <span className="um-rail-num">&mdash;</span>
                  <span>{label}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Static, author-written prose — see the note on MANUAL_HTML above. */}
        {/* eslint-disable-next-line react/no-danger */}
        <main className="um-body" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}
